import { Stack } from '@mui/material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { DefaultAccordion } from '../../generic/DefaultAccordion'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { CheckCorrectTimeFormat } from '../../generic/DateTimeFunctions'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import { apiGet } from '../../generic/Api_Functions'

const TaskToolInfoDialog = ({ open, setOpen, toolInfoList, editing, technology, isParsed, onToolInfoListUpdated }) => {
  const { t_ } = useSmartTranslation()
  const [toolReferences, setToolReferences] = useState({ milling: [], turning: [] })

  const separateToolReferencesByType = useCallback((data, getToolReference = (item) => item) => {
    const newData = { milling: [], turning: [], millingIndex: [], turningIndex: [] }
    if (!data) {
      return newData
    }
    let i = 0
    data.forEach(element => {
      if (getToolReference(element).type === 'Turning') {
        newData.turning.push(element)
        newData.turningIndex.push(i)
        i++
        return
      }
      // We assume is a mill as default
      newData.millingIndex.push(i)
      newData.milling.push(element)
      i++
    })
    return newData
  }, [])

  useEffect(() => {
    const loadTools = async () => {
      const data = await apiGet('jobLinker/partReferenceWorkflow/toolReferences/byTechnologyId/' + technology.id)
      setToolReferences(separateToolReferencesByType(data))
    }
    if (!technology) {
      return
    }
    loadTools()
  }, [technology, separateToolReferencesByType])

  const { milling, turning, millingIndex, turningIndex } = useMemo(() => {
    return separateToolReferencesByType(toolInfoList, (item) => item.toolReference)
  }, [toolInfoList, separateToolReferencesByType])

  const checkDoubleCompatible = (consumption) => {
    const number = parseFloat(consumption)
    if (!(!isNaN(number) && /^\d*\.?\d*$/.test(consumption))) {
      return t_('Introduced value is not valid')
    }
  }

  const getCommonColumns = (toolReferences) => [
    {
      name: t_('Tool'),
      field: 'toolReference.name',
      readOnly: isParsed,
      type: 'smartSelect',
      edit: {
        source: () => {
          return Promise.resolve(toolReferences)
        },
        id: 'id',
        field: 'name'
      },
      mandatory: true
    },
    {
      name: t_('Tool Number'),
      field: 'toolNumber',
      readOnly: isParsed
    },
    {
      name: t_('Duration') + ' (hh:mm:ss)',
      field: 'duration',
      condition: (rowData) => {
        return CheckCorrectTimeFormat(rowData.duration, t_)
      }
    },
    {
      name: t_('Consumption'),
      field: 'consumption',
      readOnly: (row) => {
        return isParsed
      },
      condition: (row) => {
        if (row.toolReference.unitTypeIsDuration) {
          return CheckCorrectTimeFormat(row.consumption, t_)
        } else {
          return checkDoubleCompatible(row.consumption)
        }
      }
    }
  ]

  const columnsMilling = [
    ...getCommonColumns(toolReferences.milling),
    {
      name: t_('Length'),
      field: 'length',
      numeric: 'true',
      readOnly: () => {
        return isParsed
      }
    },
    {
      name: t_('Diameter'),
      field: 'diameter',
      numeric: 'true',
      readOnly: () => {
        return isParsed
      }
    }
  ]

  const columnsTurning = [
    ...getCommonColumns(toolReferences.turning),
    {
      name: t_('ZL'),
      field: 'zaxisLength',
      numeric: 'true'
    },
    {
      name: t_('XL'),
      field: 'xaxisLength',
      numeric: 'true'
    },
    {
      name: t_('YL'),
      field: 'yaxisLength',
      numeric: 'true'
    }
  ]

  const getActions = useCallback((turn) => {
    if (!editing) {
      return {}
    }
    return {
      create: {
        fetchFunction: (row) => {
          onToolInfoListUpdated([row, ...toolInfoList])
          return Promise.resolve(row)
        }
      },
      edit: {
        fetchFunction: (row) => {
          const newList = [...toolInfoList]
          const index = turn ? turningIndex[row['smart-table-index']] : millingIndex[row['smart-table-index']]
          const newInfo = { ...row }
          newList[index] = newInfo
          onToolInfoListUpdated(newList)
          return Promise.resolve(row)
        }
      },
      delete: {
        fetchFunction: (rows) => {
          const row = rows[0]
          const newList = [...toolInfoList]
          const index = turn ? turningIndex[row['smart-table-index']] : millingIndex[row['smart-table-index']]
          newList.splice(index, 1)
          onToolInfoListUpdated(newList)
          return Promise.resolve(row)
        }
      }
    }
  }, [editing, onToolInfoListUpdated, toolInfoList, millingIndex, turningIndex])

  return (

    <SmartDialog
      setOpen={open}
      accept={false}
      cancel={false}
      close
      closeCallback={() => setOpen(false)}
      renderComponent={

        <div
          style={{ minWidth: '75vw' }}
        >
          <h2 style={{ marginBottom: '16px', textAlign: 'left' }}>
            {t_('Tool Info')}
          </h2>
          <Stack spacing={2}>
            <DefaultAccordion
              headerRender={t_('Milling tools')}
              defaultExpanded
            >
              <SmartMateriaUITable
                columns={columnsMilling}
                dataFetch={milling}
                actions={getActions(false)}
              />
            </DefaultAccordion>

            <DefaultAccordion
              headerRender={t_('Turning tools')}
              defaultExpanded={false}
            >
              <SmartMateriaUITable
                columns={columnsTurning}
                dataFetch={turning}
                actions={getActions(true)}
              />
            </DefaultAccordion>
          </Stack>
        </div>
    }
    />
  )
}

export default TaskToolInfoDialog
